import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Navigate, useNavigate } from 'react-router-dom'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import { DesktopDatePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import { useSnackbar } from 'notistack'
import useLocalStorage from 'use-local-storage'

import {
    useCalculateOrderMutation,
    useCreateOrderMutation,
    useDuplicateOrderMutation,
    useGetOrdersByCustomerQuery,
} from '@/app/services/orders'
import { selectContactId } from '@/app/slices/contactsSlice'
import { selectCustomerId } from '@/app/slices/customerSlice'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import OrderHistoryToolbar from '@/common/components/AppBar/OrderHistoryToolbar'
import SearchInput from '@/common/components/SearchInput/SearchInput'
import TbxLocalizationProvider from '@/common/components/TbxLocalizationProvider/TbxLocalizationProvider'
import {
    ORDER_STATUS_MAP,
    ORDER_USER_TYPE,
    PARTS_PER_PAGE_DEFAULT_VALUE,
    PARTS_PER_PAGE_VARIABLE,
    Paths,
} from '@/common/utils'

import NoProjectsPanel from '../order/components/NoProjectsPanel/NoProjectsPanel'

import OrdersHistoryTable from './OrdersHistory/OrdersHistoryTable'

const classes = {
    errorMessage: {
        color: 'error.main',
        marginTop: '24px',
    },
    searchInput: {
        width: '18%',
    },
    selectInput: {
        width: '12%',
        backgroundColor: 'white',
    },
    textFieldDate: {
        width: '12%',
        backgroundColor: 'white',
    },
}

const OrderHistory = () => {
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const navigate = useNavigate()
    const { isAuthenticated } = useKindeAuth()

    const organisationId = useSelector(selectOrganisationId)
    const currentCustomerId = useSelector(selectCustomerId)
    const currentContactId = useSelector(selectContactId)

    const [orderStatusFilter, setOrderStatusFilter] = useState('All')
    const [createdFromFilter, setCreatedFromFilter] = useState(undefined)
    const [createdToFilter, setCreatedToFilter] = useState(undefined)

    const userTimeZone = dayjs.tz.guess()

    const [searchTerm, setSearchTerm] = useState('')

    const [currentPage, setCurrentPage] = useState(0)
    const [currentRowsPerPage, setCurrentRowsPerPage] = useLocalStorage(
        PARTS_PER_PAGE_VARIABLE,
        PARTS_PER_PAGE_DEFAULT_VALUE
    )
    const [sortBy, setSortBy] = useState('lastModifiedDateUtc')
    const [sortDirection, setSortDirection] = useState('desc')

    const { data, isError, isLoading, refetch } = useGetOrdersByCustomerQuery({
        organisationId,
        params: {
            customerId: currentCustomerId,
            pageNumber: currentPage,
            pageSize: currentRowsPerPage,
            searchTerm: searchTerm,
            orderedByProperty: sortBy,
            orderedBy: sortDirection,
            status: orderStatusFilter,
            createdDateFrom: createdFromFilter ? dayjs.utc(createdFromFilter).format() : undefined,
            createdDateTo: createdToFilter ? dayjs.utc(createdToFilter).format() : undefined,
        },
    })

    const [duplicateOrder] = useDuplicateOrderMutation()
    const [calculateOrderMutation] = useCalculateOrderMutation()
    const [createOrder, { isLoading: isCreatingOrder, reset: resetCreateOrder }] = useCreateOrderMutation()

    const orders = data?.quotes
    const totalOrderCount = data?.count ?? 0
    const availableStatuses = data?.availableStatuses ?? []

    const cleanupStatuses = useMemo(() => {
        const mappedStatuses = availableStatuses
            ?.map((status) => ({
                label: ORDER_STATUS_MAP[status].label,
                value: status === 'NotCalculated' || status === 'Calculated' ? 'Editing' : status,
                index: ORDER_STATUS_MAP[status].index,
            }))
            ?.sort((a, b) => a.index - b.index)

        const removeDuplicates = (array, key) => {
            const seen = new Set()
            return array.filter((item) => {
                const keyValue = item[key]
                if (seen.has(keyValue)) {
                    return false
                } else {
                    seen.add(keyValue)
                    return true
                }
            })
        }

        return removeDuplicates(mappedStatuses, 'value')
    }, [availableStatuses])

    const calculateOrder = async (orderId) => {
        try {
            await calculateOrderMutation({ organisationId, orderId }).unwrap()
        } catch (error) {
            const errorMessage = t(error.data)
            enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 5000 })
        }
    }

    const handleOrderDuplicateClicked = async (id, newName) => {
        try {
            const response = await duplicateOrder({ organisationId, orderId: id, newName: newName }).unwrap()
            calculateOrder(response.id)
        } catch (error) {
            enqueueSnackbar(t('An error occurred duplicating your order'), { variant: 'error', autoHideDuration: 5000 })
        }
    }

    const handleCreateOrderClicked = async () => {
        try {
            const createdOrder = await createOrder({
                organisationId,
                payload: {
                    organisationId,
                    quoteUserType: isAuthenticated ? ORDER_USER_TYPE.Customer : ORDER_USER_TYPE.Guest,
                    customerContactId: isAuthenticated ? currentContactId : null,
                    customerId: isAuthenticated ? currentCustomerId : null,
                },
            }).unwrap()
            navigate(`/orders/${createdOrder.id}`)
        } catch (error) {
            enqueueSnackbar(t('An error occurred creating your order'), { variant: 'error', autoHideDuration: 5000 })
        } finally {
            resetCreateOrder()
        }
    }

    const handleSearchChange = (value) => {
        setSearchTerm(value)
        setCurrentPage(0)
        refetch()
    }

    const handleOrderStatusFilter = (event) => {
        setOrderStatusFilter(event.target.value)
        setCurrentPage(0)
    }

    const handleCreatedFromFilter = (date) => {
        setCreatedFromFilter(date?.utc()?.format() || null)
        setCurrentPage(0)
    }

    const handleCreatedToFilter = (date) => {
        setCreatedToFilter(date?.utc()?.format() || null)
        setCurrentPage(0)
    }

    const userHasNoOrders = !isLoading && (!orders || orders.length === 0)

    const handlePageOrderChanged = (currentPage, currentRowsPerPage, order, orderBy) => {
        setCurrentPage(currentPage)
        setCurrentRowsPerPage(currentRowsPerPage)
        setSortBy(orderBy)
        setSortDirection(order)
    }

    useEffect(() => {
        refetch()
    }, [currentPage, currentRowsPerPage, sortBy, sortDirection, createdToFilter, createdFromFilter, orderStatusFilter])

    if (!isAuthenticated) {
        return <Navigate to={Paths.HOME_PATHNAME} />
    }

    if (isAuthenticated && currentCustomerId) {
        return (
            <>
                <OrderHistoryToolbar
                    handleCreateOrderClick={handleCreateOrderClicked}
                    isLoading={isCreatingOrder}
                />
                <Grid size={12}>
                    <Typography variant="h6">{t('MY ORDERS')}</Typography>
                </Grid>
                {isError ? (
                    <Grid size={12}>
                        <Typography
                            sx={classes.errorMessage}
                            variant="body1"
                        >
                            {t('$t(An error occurred) loading your $t(orders)')}
                        </Typography>
                    </Grid>
                ) : (
                    <>
                        <Grid
                            id="page-container"
                            size={12}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                }}
                            >
                                <SearchInput
                                    label={t('Search')}
                                    placeholder={t('Search by order number')}
                                    sx={classes.searchInput}
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                />

                                <FormControl
                                    sx={classes.selectInput}
                                    variant="outlined"
                                >
                                    <InputLabel id="filter-by-status-label">{t('Filter by status')}</InputLabel>
                                    <Select
                                        id="filter-by-status"
                                        label={t('Filter by status')}
                                        labelId="filter-by-status-label"
                                        size="small"
                                        value={orderStatusFilter}
                                        onChange={handleOrderStatusFilter}
                                    >
                                        <MenuItem value="All">{t('All')}</MenuItem>
                                        {cleanupStatuses.map(({ label, value }) => (
                                            <MenuItem
                                                key={label}
                                                value={value}
                                            >
                                                {t(label)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <TbxLocalizationProvider>
                                    <DesktopDatePicker
                                        disableFuture={true}
                                        format="DD-MMM-YYYY"
                                        label={t('Created from')}
                                        slotProps={{
                                            textField: {
                                                placeholder: 'From date',
                                                size: 'small',
                                                variant: 'outlined',
                                                InputLabelProps: {
                                                    shrink: true,
                                                },
                                            },
                                            field: {
                                                clearable: true,
                                                onClear: () => {
                                                    setCreatedFromFilter(null)
                                                    setCreatedToFilter(null)
                                                },
                                            },
                                        }}
                                        sx={classes.textFieldDate}
                                        timezone={userTimeZone}
                                        value={createdFromFilter ? dayjs(createdFromFilter) : null}
                                        onAccept={handleCreatedFromFilter}
                                    />

                                    <DesktopDatePicker
                                        disabled={createdFromFilter ? false : true}
                                        disableFuture={true}
                                        format="DD-MMM-YYYY"
                                        label={t('Created to')}
                                        minDate={createdFromFilter ? dayjs(createdFromFilter) : null}
                                        slotProps={{
                                            textField: {
                                                placeholder: 'To date',
                                                size: 'small',
                                                variant: 'outlined',
                                                InputLabelProps: {
                                                    shrink: true,
                                                },
                                            },
                                            field: {
                                                clearable: true,
                                                onClear: () => {
                                                    setCreatedFromFilter(null)
                                                    setCreatedToFilter(null)
                                                },
                                            },
                                        }}
                                        sx={classes.textFieldDate}
                                        timezone={userTimeZone}
                                        value={createdToFilter ? dayjs(createdToFilter) : null}
                                        onAccept={handleCreatedToFilter}
                                    />
                                </TbxLocalizationProvider>
                            </Box>
                        </Grid>
                        <Grid
                            id="page-container"
                            size={12}
                        >
                            {userHasNoOrders ? (
                                <NoProjectsPanel />
                            ) : (
                                <OrdersHistoryTable
                                    currentPage={currentPage}
                                    filteredOrders={orders}
                                    handleOrderDuplicateClicked={handleOrderDuplicateClicked}
                                    handlePageOrderChanged={handlePageOrderChanged}
                                    isLoading={isLoading}
                                    order={sortDirection}
                                    orderBy={sortBy}
                                    totalOrderCount={totalOrderCount}
                                />
                            )}
                        </Grid>
                    </>
                )}
            </>
        )
    }
}

export default OrderHistory
